/**
 * @file VerticleCardList/component.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 * @author Kristen Elliott [kristen.elliott@cbc.ca]
 */

import PropTypes from "prop-types";
import ContentList from "../ContentList/component";
import { DISPLAY_TYPES } from "../ContentList/constants";
import MobilePolicy from "../ContentArea/library/MobilePolicy";
import DisplayPolicy from "../ContentArea/library/DisplayPolicy";
import TitleBar from "./components/TitleBar/component";
import classNames from "classnames";

/**
 *
 * @param {Object} props The component props
 * @param {String} [props.title] The title to use for the list.
 * @param {String} [props.url] The url to use as href for title.
 * @param {Object} [props.customTitleBar] A component to use in the place of the list header.
 * @param {String} [props.displayPolicy] Display policy to use for list.
 *
 * @returns {React.Component} The VerticalCardList component
 */
export function VerticalCardList({
    title = "",
    id = "",
    cardClickHandler = () => {},
    displayType = DISPLAY_TYPES.TYPE_LIST,
    baseSection = "",
    hideTitle,
    featureAttribution = null,
    items,
    displayPolicy,
    cardVariant,
    listAriaLabel,
    featureInstance,
    url,
    customTitleBar,
    className,
}) {
    let contentList = null;

    if (items) {
        const contentProps = {
            items,
            baseSection,
            loadMore: false,
            trackingSection: `verticallist${id}`,
            displayType: displayType || DISPLAY_TYPES.TYPE_LIST,
            cardClickHandler,
            cardVariant,
            listAriaLabel,
            displayPolicy:
                displayPolicy ||
                new MobilePolicy({ secondaryCardType: "TYPE_TEXT" }),
        };
        contentList = <ContentList {...contentProps} />;
    }

    const classes = classNames(
        "verticalCardList",
        "cardList", // This class name is a bit of a hack. It is needed to increase the specificity for the CSS.
        // A refactor to reduce the specificity of the contentListCards element outside this component will be needed.
        className
    );

    return (
        <div
            className={classes}
            data-feature-instance={featureInstance}
            {...featureAttribution}
            data-testid="vertical-card-list"
        >
            <TitleBar
                customTitleBar={customTitleBar}
                title={title}
                url={url}
                titleClasses={hideTitle ? "a11y" : ""}
            />
            {contentList}
        </div>
    );
}

VerticalCardList.propTypes = {
    title: PropTypes.string,
    url: PropTypes.string,
    customTitleBar: PropTypes.object,
    items: PropTypes.array,
    displayType: PropTypes.oneOf(Object.values(DISPLAY_TYPES)),
    displayPolicy: PropTypes.instanceOf(DisplayPolicy),
    cardClickHandler: PropTypes.func,
    baseSection: PropTypes.string,
    hideTitle: PropTypes.bool, // Visually hides H1 tag with a11y class, will be seen by assistive technologies
    featureInstance: PropTypes.string,
    featureAttribution: PropTypes.object,
};

export default VerticalCardList;
